import { FC, FocusEvent, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { EditorContent } from '@tiptap/react';
import { FormattedMessage, unsafeTranslationKey } from 'intl';

import { RichTextToolbar } from './Toolbar';
import { isInsideRichTextPopup } from './richTextPopup';
import { RICH_TEXT_VARIANTS, richTextFieldIds } from './model';
import { RichTextEditorPresentationalProps } from './types';
import {
  editorWrapperSx,
  editorContentSx,
  helperTextSx,
  labelAboveFieldSx,
  richTextRootSx,
  richTextRootDenseSx,
} from './styles';

export const RichTextEditorPresentational: FC<RichTextEditorPresentationalProps> = ({
  editor,
  characterCount,
  isOverLimit,
  maxLength = 10000,
  minRows = 3,
  autoGrow = false,
  disabled,
  error,
  labelId,
  dataQa,
  fieldName,
  dense,
  variant = 'block',
  label,
  required,
}) => {
  const [focused, setFocused] = useState(false);
  const hasError = error || isOverLimit;
  const { fieldId, labelElementId } = richTextFieldIds({ dataQa, fieldName });

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  // The colour picker belongs to the field: the field stays outlined as focused while it is used.
  const handleBlur = useCallback((event: FocusEvent<HTMLElement>) => {
    if (!isInsideRichTextPopup(event.relatedTarget)) {
      setFocused(false);
    }
  }, []);

  return (
    <Box data-qa={dataQa} sx={dense ? richTextRootDenseSx : richTextRootSx}>
      {(labelId || label) && (
        // Not `htmlFor`: the editable text is no form control; it names itself with `aria-labelledby`.
        <Typography
          component="label"
          id={labelElementId}
          onClick={() => editor?.commands.focus()}
          sx={labelAboveFieldSx(hasError, focused)}
        >
          {label ?? <FormattedMessage id={unsafeTranslationKey(labelId ?? '')} />}
          {required && ' *'}
        </Typography>
      )}
      <Box
        data-rich-text-wrapper='true'
        id={fieldId}
        sx={editorWrapperSx(hasError, focused)}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <RichTextToolbar editor={editor} disabled={disabled} error={hasError} variant={variant} />
        <Box
          data-rich-text-content='true'
          sx={editorContentSx(hasError, disabled, minRows, autoGrow)}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>
      {RICH_TEXT_VARIANTS[variant].showsCharacterCount && (
        <Typography data-rich-text-helper='true' sx={helperTextSx(isOverLimit)}>
          {characterCount} / {maxLength}
        </Typography>
      )}
    </Box>
  );
};
